import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api, { getAuthToken, setAuthToken, setStoredUser } from "@/lib/api";
import { getCurrentUser, setAdminAuthenticated, setStaffAuth } from "@/lib/store";
import { Plus, Trash2, Key, UserCog, LogIn } from "lucide-react";

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  const [newUser, setNewUser] = useState({
    username: "",
    password: "",
    role: "kitchen",
    property_id: "",
    whatsapp_numbers: [],
    language: "en",
    receive_whatsapp_notifications: false,
    can_manage_discounts: false
  });

  const [newPassword, setNewPassword] = useState("");
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editUser, setEditUser] = useState(null);

  useEffect(() => {
    setCurrentUser(getCurrentUser());
    fetchData();
  }, []);

  const isAdminOrGlobal = 
    currentUser?.role === "admin" || 
    currentUser?.role === "global" || 
    localStorage.getItem("hm_admin_auth") === "true";

  const fetchData = async () => {
    try {
      const [usersRes, propsRes] = await Promise.all([
        api.get("/users"),
        api.get("/settings")
      ]);
      setUsers(usersRes.data || []);
      setProperties(propsRes.data || []);
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const validatePasswordComplexity = (password, username = "") => {
    if (!password || password.length < 8) {
      return "Password must be at least 8 characters long";
    }
    if (!/[a-z]/.test(password)) {
      return "Password must contain at least one lowercase letter";
    }
    if (!/[A-Z]/.test(password)) {
      return "Password must contain at least one uppercase letter";
    }
    if (!/[0-9]/.test(password)) {
      return "Password must contain at least one number";
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};\x27:"\\|,.<>/?`~]/.test(password)) {
      return "Password must contain at least one special character (!@#$%^&* etc.)";
    }
    if (username && username.trim().length >= 3 && password.toLowerCase().includes(username.trim().toLowerCase())) {
      return "Password cannot contain your username";
    }
    return null;
  };

  const handleAddUser = async () => {
    if (!newUser.username || !newUser.password) {
      toast.error("Username and password required");
      return;
    }
    if (/\s/.test(newUser.username)) {
      toast.error("Username cannot contain spaces. Use letters, numbers, underscores, or hyphens.");
      return;
    }
    const pwdErr = validatePasswordComplexity(newUser.password, newUser.username);
    if (pwdErr) {
      toast.error(pwdErr);
      return;
    }
    if (newUser.role !== "admin" && newUser.role !== "global" && !newUser.property_id) {
      toast.error("Please select a property for this team member");
      return;
    }

    try {
      await api.post("/auth/register", newUser);
      toast.success("User created successfully");
      setShowAddDialog(false);
      setNewUser({
        username: "",
        password: "",
        role: "kitchen",
        property_id: "",
        whatsapp_numbers: [],
        language: "en",
        receive_whatsapp_notifications: false,
        can_manage_discounts: false
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const handleEditUser = async () => {
    if (!editUser.username) {
      toast.error("Username required");
      return;
    }
    if (/\s/.test(editUser.username)) {
      toast.error("Username cannot contain spaces. Use letters, numbers, underscores, or hyphens.");
      return;
    }
    if (editUser.role !== "admin" && editUser.role !== "global" && !editUser.property_id) {
      toast.error("Please select a property for this team member");
      return;
    }

    try {
      await api.put(`/users/${editUser.id}`, editUser);
      toast.success("User updated successfully");
      setShowEditDialog(false);
      setEditUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Delete this user?")) return;

    try {
      await api.delete(`/users/${userId}`);
      toast.success("User deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const handleChangePassword = async () => {
    const pwdErr = validatePasswordComplexity(newPassword, selectedUser?.username);
    if (pwdErr) {
      toast.error(pwdErr);
      return;
    }

    try {
      await api.put(`/users/${selectedUser.id}/password`, {
        new_password: newPassword
      });
      toast.success(`Password updated for ${selectedUser.username}`);
      setShowPasswordDialog(false);
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update password");
    }
  };

  const handleImpersonate = async (targetUser) => {
    try {
      const adminToken = getAuthToken();
      const adminUser = getCurrentUser() || { id: "admin", username: "Tech", role: "admin" };

      localStorage.setItem("hm_impersonator", JSON.stringify({
        adminToken,
        adminUser
      }));

      const res = await api.post(`/admin/impersonate/${targetUser.id}`);
      if (res.data?.token) {
        setAuthToken(res.data.token);
        setStoredUser(res.data.user);

        if (res.data.user.role === "kitchen" || res.data.user.role === "manager" || res.data.user.role === "staff") {
          localStorage.removeItem("hm_admin_auth");
          setStaffAuth(res.data.user);
          window.dispatchEvent(new Event("impersonationChanged"));
          toast.success(`Logged in as ${targetUser.username}`);
          navigate(`/team/dashboard?property=${targetUser.property_id || ""}`);
        } else {
          setAdminAuthenticated(true);
          window.dispatchEvent(new Event("impersonationChanged"));
          toast.success(`Logged in as ${targetUser.username}`);
          navigate("/admin/dashboard");
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to impersonate user");
    }
  };

  const getRoleBadge = (role) => {
    const roleLabels = { admin: "Admin Monkey", global: "Global Monkey", manager: "Manager Monkey", kitchen: "Kitchen Monkey" };
    const colors = {
      admin: "bg-purple-100 text-purple-800",
      global: "bg-indigo-100 text-indigo-800",
      manager: "bg-blue-100 text-blue-800",
      kitchen: "bg-green-100 text-green-800"
    };
    return <Badge className={colors[role] || "bg-gray-100"}>{roleLabels[role] || role}</Badge>;
  };

  const filteredUsers = users.filter(user => {
    if (filterType === "all") return true;
    if (filterType === "global_admin") return user.role === "admin" || user.role === "global";
    return user.property_id === filterType;
  });

  return (
    <AdminLayout title="Team Management">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-semibold text-[#264653]">Team Accounts</h2>
            <p className="text-xs text-gray-500 mt-0.5">Manage permissions, WhatsApp alerts, and logins</p>
          </div>

          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full sm:w-[180px] bg-white">
                <SelectValue placeholder="Filter Users" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Team Members</SelectItem>
                <SelectItem value="global_admin">Global & Admins</SelectItem>
                {properties.map(p => (
                  <SelectItem key={p.property_id} value={p.property_id}>{p.property_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="bg-[#2A9D8F] w-full sm:w-auto">
                  <Plus className="w-4 h-4 mr-2" /> Add Team Member
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg w-[95vw] max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add New Team Member</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Username (No spaces)</Label>
                    <Input
                      value={newUser.username}
                      onChange={(e) => setNewUser({ ...newUser, username: e.target.value.replace(/\s/g, "") })}
                      placeholder="e.g. rahul_varanasi"
                    />
                    <p className="text-[11px] text-gray-500 mt-1">Letters, numbers, underscores, and hyphens only.</p>
                  </div>

                  <div>
                    <Label>Password (Strong password required)</Label>
                    <Input
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      placeholder="Min 8 chars with uppercase, number & symbol"
                    />
                    <p className="text-[11px] text-gray-500 mt-1">Min 8 characters, 1 uppercase, 1 lowercase, 1 digit, 1 special character.</p>
                  </div>

                  <div>
                    <Label>Role</Label>
                    <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {isAdminOrGlobal && <SelectItem value="admin">Admin Monkey (All Properties)</SelectItem>}
                        {isAdminOrGlobal && <SelectItem value="global">Global Monkey (All Properties)</SelectItem>}
                        <SelectItem value="manager">Manager Monkey</SelectItem>
                        <SelectItem value="kitchen">Kitchen Monkey</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Preferred Language</Label>
                    <Select value={newUser.language} onValueChange={(v) => setNewUser({ ...newUser, language: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">English (Default)</SelectItem>
                        <SelectItem value="hi">Hindi</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {newUser.role !== "admin" && newUser.role !== "global" && (
                    <div>
                      <Label>Property</Label>
                      <Select value={newUser.property_id || undefined} onValueChange={(v) => setNewUser({ ...newUser, property_id: v })}>
                        <SelectTrigger><SelectValue placeholder="Select property" /></SelectTrigger>
                        <SelectContent>
                          {properties.map((p) => (
                            <SelectItem key={p.property_id} value={p.property_id}>
                              {p.property_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div>
                    <Label>WhatsApp Numbers (comma separated)</Label>
                    <Input
                      value={newUser.whatsapp_numbers.join(", ")}
                      onChange={(e) => setNewUser({
                        ...newUser,
                        whatsapp_numbers: e.target.value.split(",").map(n => n.trim()).filter(n => n)
                      })}
                      placeholder="+919876543210"
                    />
                  </div>

                  {/* Selective WhatsApp Alert Toggle */}
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-100 flex items-center justify-between">
                    <div>
                      <Label className="font-semibold text-xs">Receive WhatsApp Alerts</Label>
                      <p className="text-[11px] text-gray-500">Enable booking, order & service WhatsApp notifications (Default OFF for test accounts)</p>
                    </div>
                    <Switch
                      disabled={!isAdminOrGlobal}
                      checked={newUser.receive_whatsapp_notifications}
                      onCheckedChange={(c) => setNewUser({ ...newUser, receive_whatsapp_notifications: c })}
                    />
                  </div>

                  <Button onClick={handleAddUser} className="w-full bg-[#2A9D8F] hover:bg-[#218377] text-white">
                    Add Team Member
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : filteredUsers.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-gray-500">
              No team members found. Add your first team member.
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredUsers.map((user) => (
              <Card key={user.id} className="border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-11 h-11 rounded-2xl bg-[#2A9D8F] flex items-center justify-center text-white font-bold text-lg shadow-sm flex-shrink-0">
                      {user.username.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-bold text-base text-[#264653] truncate">{user.username}</p>
                        {user.role === "admin" && (
                          <Badge className="bg-purple-100 text-purple-800 border-purple-200 text-xs">
                            Master Admin
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        {getRoleBadge(user.role)}
                        <Badge variant="outline" className="text-gray-500 text-xs">
                          {user.language === "hi" ? "Hindi" : "English"}
                        </Badge>
                        {user.property_id && user.role !== "admin" && user.role !== "global" && (
                          <span className="text-xs text-gray-500 font-medium bg-gray-100 px-2 py-0.5 rounded-md">
                            {user.property_id}
                          </span>
                        )}
                        {user.receive_whatsapp_notifications ? (
                          <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs">
                            📱 Alerts: ON
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-gray-400 border-gray-200 text-xs">
                            🔕 Alerts: OFF
                          </Badge>
                        )}
                      </div>
                      {user.whatsapp_numbers?.length > 0 && (
                        <p className="text-xs text-gray-400 mt-1 truncate">
                          📱 {user.whatsapp_numbers.join(", ")}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 self-start md:self-center">
                    {/* Impersonate "Login as User" button */}
                    {isAdminOrGlobal && user.id !== currentUser?.id && user.username !== currentUser?.username && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleImpersonate(user)}
                        className="flex items-center gap-1.5 border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100 shadow-sm"
                        title={`Login and view app as ${user.username}`}
                        data-testid={`impersonate-${user.username}`}
                      >
                        <LogIn className="w-3.5 h-3.5 text-amber-700" />
                        <span className="text-xs font-semibold">Login as User</span>
                      </Button>
                    )}

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditUser({ ...user });
                        setShowEditDialog(true);
                      }}
                      className="flex items-center gap-1.5 border-[#2A9D8F]/30 text-[#2A9D8F] hover:bg-[#2A9D8F]/10"
                    >
                      <UserCog className="w-3.5 h-3.5" />
                      <span className="text-xs font-semibold">Edit</span>
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedUser(user);
                        setShowPasswordDialog(true);
                      }}
                      className="flex items-center gap-1.5 border-[#2A9D8F]/30 text-[#2A9D8F] hover:bg-[#2A9D8F]/10"
                    >
                      <Key className="w-3.5 h-3.5" />
                      <span className="text-xs font-semibold">Reset Password</span>
                    </Button>

                    {user.username?.toLowerCase() !== "admin" && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteUser(user.id)}
                        title="Delete User"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit User Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-lg w-[95vw] max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Team Member</DialogTitle>
            </DialogHeader>
            {editUser && (
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Username (No spaces)</Label>
                  <Input
                    value={editUser.username}
                    onChange={(e) => setEditUser({ ...editUser, username: e.target.value.replace(/\s/g, "") })}
                  />
                  <p className="text-[11px] text-gray-500 mt-1">Letters, numbers, underscores, and hyphens only.</p>
                </div>
                <div>
                  <Label>Role</Label>
                  <Select value={editUser.role} onValueChange={(v) => setEditUser({ ...editUser, role: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {isAdminOrGlobal && <SelectItem value="admin">Admin Monkey</SelectItem>}
                      {isAdminOrGlobal && <SelectItem value="global">Global Monkey</SelectItem>}
                      <SelectItem value="manager">Manager Monkey</SelectItem>
                      <SelectItem value="kitchen">Kitchen Monkey</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {editUser.role !== "admin" && editUser.role !== "global" && (
                  <div>
                    <Label>Property</Label>
                    <Select value={editUser.property_id || undefined} onValueChange={(v) => setEditUser({ ...editUser, property_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Select property" /></SelectTrigger>
                      <SelectContent>
                        {properties.map((p) => (
                          <SelectItem key={p.property_id} value={p.property_id}>{p.property_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>WhatsApp Numbers</Label>
                  <Input
                    value={editUser.whatsapp_numbers?.join(", ") || ""}
                    onChange={(e) => setEditUser({
                      ...editUser,
                      whatsapp_numbers: e.target.value.split(",").map(n => n.trim()).filter(n => n)
                    })}
                  />
                </div>

                {/* Selective WhatsApp Alert Toggle */}
                <div className="p-3 bg-gray-50 rounded-xl border border-gray-100 flex items-center justify-between">
                  <div>
                    <Label className="font-semibold text-xs">Receive WhatsApp Alerts</Label>
                    <p className="text-[11px] text-gray-500">
                      {isAdminOrGlobal ? "Turn ON/OFF booking & order notifications for this user" : "Only Admin or Global accounts can change alert delivery settings"}
                    </p>
                  </div>
                  <Switch
                    disabled={!isAdminOrGlobal}
                    checked={editUser.receive_whatsapp_notifications}
                    onCheckedChange={(c) => setEditUser({ ...editUser, receive_whatsapp_notifications: c })}
                  />
                </div>

                {/* Granular Permissions (Editable ONLY by Admin / Global) */}
                {(editUser.role === "manager" || editUser.role === "global") && (
                  <div className="pt-4 border-t space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-sm">Access Permissions</h3>
                      {!isAdminOrGlobal && <span className="text-xs text-amber-700 font-medium">(Admin Only)</span>}
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Manage Coupons & Discounts</Label>
                      <Switch disabled={!isAdminOrGlobal} checked={editUser.can_manage_discounts} onCheckedChange={(c) => setEditUser({...editUser, can_manage_discounts: c})} />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>View Reports</Label>
                      <Switch disabled={!isAdminOrGlobal} checked={editUser.can_view_reports} onCheckedChange={(c) => setEditUser({...editUser, can_view_reports: c})} />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>View Analytics</Label>
                      <Switch disabled={!isAdminOrGlobal} checked={editUser.can_view_analytics} onCheckedChange={(c) => setEditUser({...editUser, can_view_analytics: c})} />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Edit Menu & Availability</Label>
                      <Switch disabled={!isAdminOrGlobal} checked={editUser.can_edit_menu} onCheckedChange={(c) => setEditUser({...editUser, can_edit_menu: c})} />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Manage Team Members</Label>
                      <Switch disabled={!isAdminOrGlobal} checked={editUser.can_manage_users} onCheckedChange={(c) => setEditUser({...editUser, can_manage_users: c})} />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Change Property Settings & Banners</Label>
                      <Switch disabled={!isAdminOrGlobal} checked={editUser.can_manage_settings} onCheckedChange={(c) => setEditUser({...editUser, can_manage_settings: c})} />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Manage Experiences</Label>
                      <Switch disabled={!isAdminOrGlobal} checked={editUser.can_manage_experiences} onCheckedChange={(c) => setEditUser({...editUser, can_manage_experiences: c})} />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Manage QR Codes</Label>
                      <Switch disabled={!isAdminOrGlobal} checked={editUser.can_manage_qrcodes} onCheckedChange={(c) => setEditUser({...editUser, can_manage_qrcodes: c})} />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>View System Logs</Label>
                      <Switch disabled={!isAdminOrGlobal} checked={editUser.can_view_logs} onCheckedChange={(c) => setEditUser({...editUser, can_view_logs: c})} />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Edit Message Templates</Label>
                      <Switch disabled={!isAdminOrGlobal} checked={editUser.can_edit_messages} onCheckedChange={(c) => setEditUser({...editUser, can_edit_messages: c})} />
                    </div>
                  </div>
                )}

                <Button onClick={handleEditUser} className="w-full bg-[#2A9D8F] hover:bg-[#218377] text-white">
                  Save Changes
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Password Change Dialog */}
        <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
          <DialogContent className="max-w-md w-[95vw]">
            <DialogHeader>
              <DialogTitle>Change Password for {selectedUser?.username}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>New Password (Strict Policy)</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter strong password"
                />
                <div className="mt-2 p-2.5 bg-gray-50 rounded-lg text-xs space-y-1 text-gray-600 border border-gray-100">
                  <p className="font-semibold text-gray-700">Password Requirements:</p>
                  <p>• At least 8 characters</p>
                  <p>• At least 1 uppercase letter (A-Z)</p>
                  <p>• At least 1 lowercase letter (a-z)</p>
                  <p>• At least 1 number (0-9)</p>
                  <p>• At least 1 special character (!@#$%^&* etc.)</p>
                  <p>• Cannot contain username</p>
                </div>
              </div>
              <Button onClick={handleChangePassword} className="w-full bg-[#2A9D8F] hover:bg-[#238276] text-white">
                Update Password
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
